<template>
	<ValidationObserver v-slot="{ handleSubmit }" slim>
		<form class="company_data_step" novalidate @submit.prevent="handleSubmit(emitContinue)">
			<h1 class="step_title">Datos de la empresa</h1>
			<p class="step_subtitle">Cuéntanos sobre tu empresa</p>
			<ValidationProvider
				v-slot="{ errors }"
				name="nombre de la empresa"
				rules="required|min:2|max:149"
				slim
			>
				<div class="field">
					<label class="field_label" for="register_company_name">
						Nombre de la empresa*
					</label>
					<input
						id="register_company_name"
						v-model.trim="form.company_name"
						class="field_input"
						:class="{ 'has-error': errors[0] }"
						type="text"
						placeholder="Ej. CFOremoto"
						autocomplete="organization"
					/>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</div>
			</ValidationProvider>
			<div class="field">
				<label class="checkbox_option">
					<input
						v-model="form.is_foreign"
						class="checkbox_input"
						type="checkbox"
						name="register_is_foreign"
					/>
					<span class="checkbox_mark"></span>
					Empresa extranjera (sin RUT chileno)
				</label>
			</div>
			<ValidationProvider
				v-if="!form.is_foreign"
				v-slot="{ errors }"
				name="rut de la empresa"
				rules="required|min:11|max:12|rut"
				mode="eager"
				slim
			>
				<div class="field">
					<label class="field_label" for="register_company_rut">
						RUT de la empresa*
					</label>
					<input
						id="register_company_rut"
						v-model.trim="form.company_rut"
						v-mask="rutMask"
						class="field_input"
						:class="{ 'has-error': errors[0] }"
						type="text"
						placeholder="Ej. 77.987.654-3"
					/>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</div>
			</ValidationProvider>
			<ValidationProvider
				v-else
				v-slot="{ errors }"
				name="identificador fiscal"
				rules="required|min:4|max:24"
				mode="eager"
				slim
			>
				<div class="field">
					<label class="field_label" for="register_company_tax_id">
						Identificador fiscal (RUT, RUC, etc.)*
					</label>
					<input
						id="register_company_tax_id"
						v-model.trim="form.company_rut"
						class="field_input"
						:class="{ 'has-error': errors[0] }"
						type="text"
						placeholder="Ej. 30-71234567-8"
					/>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</div>
			</ValidationProvider>
			<ValidationProvider v-slot="{ errors }" name="qué vende tu empresa" rules="required" slim>
				<fieldset class="field business_type_field">
					<legend class="field_label">¿Qué vende tu empresa? *</legend>
					<p class="field_help">Marca la opción que corresponda.</p>
					<label
						v-for="option in businessTypeOptions"
						:key="option"
						class="radio_option"
					>
						<input
							v-model="form.business_type"
							class="radio_input"
							type="radio"
							name="register_business_type"
							:value="option"
						/>
						<span class="radio_mark"></span>
						{{ option }}
					</label>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</fieldset>
			</ValidationProvider>
			<fieldset v-if="isProducts" class="field business_type_field">
				<legend class="field_label">
					¿Cómo opera tu empresa con los productos? *
				</legend>
				<p class="field_help">Marca la opción que corresponda.</p>
				<label class="radio_option">
					<input
						v-model="productAnswers.has_production_plant"
						class="radio_input"
						type="radio"
						name="register_product_operation"
						:value="false"
					/>
					<span class="radio_mark"></span>
					Compro y vendo productos
				</label>
				<label class="radio_option">
					<input
						v-model="productAnswers.has_production_plant"
						class="radio_input"
						type="radio"
						name="register_product_operation"
						:value="true"
					/>
					<span class="radio_mark"></span>
					Tengo una planta o un maquilador
				</label>
			</fieldset>
			<fieldset v-if="isServices" class="field business_type_field">
				<legend class="field_label">
					¿Cómo opera tu empresa con los servicios? *
				</legend>
				<p class="field_help">Marca la opción que corresponda.</p>
				<label
					v-for="option in serviceTypeOptions"
					:key="option.value"
					class="radio_option"
				>
					<input
						v-model="serviceAnswers.service_type"
						class="radio_input"
						type="radio"
						name="register_service_operation"
						:value="option.value"
					/>
					<span class="radio_mark"></span>
					{{ option.label }}
				</label>
			</fieldset>
			<button
				class="continue_button"
				type="submit"
				:disabled="!isComplete || loading"
			>
				{{ loading ? "Creando tu cuenta…" : "Continuar" }}
			</button>
		</form>
	</ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";

// Define el tipo de negocio (afecta fórmulas de margen/EBITDA en reportes).
export const BUSINESS_TYPE_OPTIONS = ["Productos", "Servicios"];

// Respuestas de Servicios: value = nombre del BusinessType del catálogo
// (el backend lo resuelve tal cual en resolve_onboarding_business_type).
// Labels en primera persona, como los de Productos; la sigla SaaS queda como
// ancla de reconocimiento para el fundador de software.
export const SERVICE_TYPE_OPTIONS = [
	{ value: "SaaS", label: "Arriendo mi software (SaaS)" },
	{ value: "Intermediario", label: "Gano comisiones" },
	{ value: "Consultor", label: "Hago asesorías" },
];

export default {
	name: "RegisterCompanyDataStep",
	components: {
		ValidationObserver,
		ValidationProvider,
	},
	props: {
		value: {
			type: Object,
			default: () => ({}),
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		const initBool = (v) => (typeof v === "boolean" ? v : null);
		return {
			form: {
				company_name: this.value.company_name || "",
				company_rut: this.value.company_rut || "",
				business_type: this.value.business_type || "",
				// Empresa extranjera: sin RUT chileno ni paso SII; el
				// identificador fiscal es el de su país y se valida genérico
				// (el backend re-valida con TaxIdValidator).
				is_foreign: this.value.is_foreign === true,
			},
			// Solo aplica a "Productos": elección excluyente (radio). null = sin
			// responder. false = compro y vendo (Comercializador), true = planta
			// o maquilador (Productor).
			productAnswers: {
				has_production_plant: initBool(this.value.has_production_plant),
			},
			// Solo aplica a "Servicios": elección excluyente (radio). "" = sin
			// responder; el value es el nombre del BusinessType.
			serviceAnswers: {
				service_type: this.value.service_type || "",
			},
			businessTypeOptions: BUSINESS_TYPE_OPTIONS,
			serviceTypeOptions: SERVICE_TYPE_OPTIONS,
			// Mismo formato que FormRegisterFast; B = dígito verificador (0-9/K).
			rutMask: ["#.###.###-B", "##.###.###-B"],
		};
	},
	watch: {
		// Al cambiar de universo el identificador tipeado no aplica (máscara y
		// validación distintas): se limpia para no arrastrar un valor a medias.
		"form.is_foreign"() {
			this.form.company_rut = "";
		},
	},
	computed: {
		isProducts() {
			return this.form.business_type === "Productos";
		},
		isServices() {
			return this.form.business_type === "Servicios";
		},
		isComplete() {
			const baseComplete = Object.values(this.form).every(
				(field) => field !== ""
			);
			if (!baseComplete) return false;
			// Ambos tipos exigen elegir cómo opera la empresa.
			if (this.isProducts) {
				return this.productAnswers.has_production_plant !== null;
			}
			if (this.isServices) {
				return this.serviceAnswers.service_type !== "";
			}
			return true;
		},
	},
	methods: {
		emitContinue() {
			// El RUT viaja como se ve (con puntos); se limpia recién en el submit
			// final del wizard. has_production_plant viaja como booleano y
			// service_type solo con valor para Servicios (el backend resuelve el
			// BusinessType con la respuesta del tipo elegido e ignora la otra).
			this.$emit("continue", {
				...this.form,
				has_production_plant:
					this.productAnswers.has_production_plant === true,
				service_type: this.isServices
					? this.serviceAnswers.service_type
					: "",
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.business_type_field {
	border: none;
	margin: 0 0 20px;
	padding: 0;
}

.radio_option {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 12px;
	color: var(--auth-text);
	font-size: 14px;
	cursor: pointer;
	user-select: none;
}

// Radio nativo oculto pero accesible (focus/teclado siguen funcionando);
// el círculo visible es .radio_mark.
.radio_input {
	position: absolute;
	opacity: 0;
	width: 16px;
	height: 16px;
	margin: 0;
	cursor: pointer;
}

.radio_mark {
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	border: 1.5px solid var(--auth-control-border);
	border-radius: 50%;
	background: var(--auth-control-bg);
	transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out;
}

.radio_input:checked + .radio_mark {
	border-color: var(--auth-accent);
	border-width: 5px;
	background: var(--auth-surface);
}

.radio_input:focus-visible + .radio_mark {
	box-shadow: 0 0 0 3px var(--auth-control-ring);
}

// Checkbox con el mismo patrón que los radios: input nativo oculto pero
// accesible, marca visible en .checkbox_mark.
.checkbox_option {
	display: flex;
	align-items: center;
	gap: 10px;
	color: var(--auth-text);
	font-size: 14px;
	cursor: pointer;
	user-select: none;
}

.checkbox_input {
	position: absolute;
	opacity: 0;
	width: 16px;
	height: 16px;
	margin: 0;
	cursor: pointer;
}

.checkbox_mark {
	flex-shrink: 0;
	position: relative;
	width: 16px;
	height: 16px;
	border: 1.5px solid var(--auth-control-border);
	border-radius: 4px;
	background: var(--auth-control-bg);
	transition: border-color 0.15s ease-out, background 0.15s ease-out,
		box-shadow 0.15s ease-out;
}

.checkbox_input:checked + .checkbox_mark {
	border-color: var(--auth-accent);
	background: var(--auth-accent);

	// Tilde dibujado con bordes (sin asset): rectángulo rotado 45°.
	&::after {
		content: "";
		position: absolute;
		top: 1px;
		left: 4.5px;
		width: 5px;
		height: 9px;
		border: solid var(--auth-surface);
		border-width: 0 2px 2px 0;
		transform: rotate(45deg);
	}
}

.checkbox_input:focus-visible + .checkbox_mark {
	box-shadow: 0 0 0 3px var(--auth-control-ring);
}
</style>
